import { createContext, useContext, type ReactNode } from "react";

export type SiteLocale = "it" | "en";

interface I18nContextType {
  locale: SiteLocale;
  t: (key: string) => string;
  prefix: string;
}

const I18nContext = createContext<I18nContextType>({
  locale: "it",
  t: (key) => key,
  prefix: "/it",
});

export function useI18n() {
  return useContext(I18nContext);
}

export function I18nProvider({ locale, children }: { locale: SiteLocale; children: ReactNode }) {
  const translations = allTranslations[locale] || allTranslations.it;
  const t = (key: string): string => translations[key] || allTranslations.it[key] || key;
  const prefix = `/${locale}`;

  return (
    <I18nContext.Provider value={{ locale, t, prefix }}>
      {children}
    </I18nContext.Provider>
  );
}

const allTranslations: Record<SiteLocale, Record<string, string>> = {
  it: {
    // Header
    "nav.summarize": "Riassumi",
    "nav.paraphrase": "Parafrasa",
    "nav.wordcount": "Conta Parole",
    "nav.simplify": "Semplifica",
    "nav.essayshortener": "Accorcia",
    "nav.grammar": "Grammatica",
    "nav.rewriter": "Riscrivi",
    "nav.aiEditor": "Editor IA",
    "nav.blog": "Blog",
    "nav.signIn": "Accedi",
    "nav.signOut": "Esci",
    "nav.dashboard": "Dashboard",
    // Hero — benefit-oriented
    "hero.badge": "Gratis · Senza registrazione · Testo non conservato",
    "hero.title": "Riassumi un testo online, gratis e senza registrazione",
    "hero.subtitle": "Incolla un testo, un link o un PDF e ottieni un riassunto in pochi secondi. Punti chiave, bullet point o TL;DR: scegli formato, lunghezza e tono.",
    // Tool
    "tool.tab.text": "📝 Testo",
    "tool.tab.url": "🔗 URL",
    "tool.text.label": "Testo da riassumere",
    "tool.text.placeholder": "Incolla qui il testo da riassumere — appunti, articoli, capitoli, email...",
    "tool.url.label": "URL dell'articolo",
    "tool.url.placeholder": "https://esempio.com/articolo",
    "tool.url.hint": "Inserisci l'URL di un articolo e lo riassumeremo automaticamente.",
    "tool.length": "Lunghezza",
    "tool.length.short": "Breve",
    "tool.length.medium": "Medio",
    "tool.length.long": "Lungo",
    "tool.tone": "Tono",
    "tool.tone.simple": "Semplice",
    "tool.tone.academic": "Accademico",
    "tool.tone.professional": "Professionale",
    "tool.format": "Formato output",
    "tool.format.paragraph": "Paragrafo",
    "tool.format.bullets": "Punti elenco",
    "tool.format.keysentences": "Frasi chiave",
    "tool.format.tldr": "TL;DR",
    "tool.language": "Lingua",
    "tool.submit": "Riassumi ora",
    "tool.loading": "Analisi in corso…",
    "tool.result.title": "Il tuo riassunto",
    "tool.result.words": "parole",
    "tool.copy": "Copia",
    "tool.copied": "Copiato!",
    "tool.minchars": "Minimo {min} caratteri",
    "tool.maxchars": "Massimo {max} caratteri",
    "tool.error.backend": "Configurazione del backend mancante.",
    "tool.error.network": "Errore di rete. Controlla la connessione e riprova.",
    "tool.error.default": "Errore durante il riassunto. Riprova.",
    // HowItWorks
    "how.title": "Come funziona",
    "how.subtitle": "Tre passaggi, cinque secondi, zero complicazioni",
    "how.step1.title": "Incolla il testo",
    "how.step1.desc": "Copia da PDF, siti web, appunti o email. Oppure incolla un URL.",
    "how.step2.title": "Scegli lo stile",
    "how.step2.desc": "Lunghezza, tono, formato e lingua. Tu decidi come vuoi il risultato.",
    "how.step3.title": "Ottieni il riassunto",
    "how.step3.desc": "L'IA genera il riassunto in secondi. Copia, scarica PDF o condividi.",
    // Meta
    "meta.title": "Riassumi Testo Online Gratis con IA | Riassunto Automatico",
    "meta.description": "Riassumi testi online gratis con intelligenza artificiale. Ottieni riassunti automatici veloci e precisi in pochi secondi.",
    // CTA
    "cta.title": "Prova RiassumiTesto gratis",
    "cta.subtitle": "Riassumi qualsiasi testo in pochi secondi con la nostra IA.",
    "cta.button": "Riassumi ora →",
    // Blog
    "blog.title": "Blog",
    "blog.badge": "Guide & Approfondimenti",
    "blog.subtitle": "Consigli, tecniche e approfondimenti su come riassumere testi e studiare meglio.",
    "blog.read": "Leggi l'articolo",
    // Footer
    "footer.tagline": "Riassumi testi con l'IA",
    "footer.guide": "Guida Completa",
    "footer.rights": "Tutti i diritti riservati.",
    "footer.desc": "Strumento gratuito per il riassunto di testi con intelligenza artificiale.",
    // Parafrasatore
    "paraphrase.title": "Parafrasatore Online Gratis",
    "paraphrase.subtitle": "Riscrivi il tuo testo con parole diverse mantenendo il significato originale.",
    // Contatore
    "wordcount.title": "Contatore Parole Online",
    "wordcount.subtitle": "Conta parole, caratteri, frasi e paragrafi del tuo testo in tempo reale.",
    // Semplificatore
    "simplify.title": "Semplifica Testo Online",
    "simplify.subtitle": "Trasforma testi complessi in versioni semplici e comprensibili.",
  },
  en: {
    // Header
    "nav.summarize": "Summarize",
    "nav.paraphrase": "Paraphrase",
    "nav.wordcount": "Word Count",
    "nav.simplify": "Simplify",
    "nav.essayshortener": "Shortener",
    "nav.grammar": "Grammar",
    "nav.rewriter": "Rewriter",
    "nav.aiEditor": "AI Editor",
    "nav.blog": "Blog",
    "nav.signIn": "Sign In",
    "nav.signOut": "Sign Out",
    "nav.dashboard": "Dashboard",
    // Hero — benefit-oriented
    "hero.badge": "Free · No signup · Text not stored",
    "hero.title": "Summarize any text online, free and without an account",
    "hero.subtitle": "Summarize long texts with AI in seconds. Key points, bullet points or TL;DR — you choose the format, length and tone.",
    // Tool
    "tool.tab.text": "📝 Text",
    "tool.tab.url": "🔗 URL",
    "tool.text.label": "Text to summarize",
    "tool.text.placeholder": "Paste your text here — notes, articles, chapters, emails...",
    "tool.url.label": "Article URL",
    "tool.url.placeholder": "https://example.com/article",
    "tool.url.hint": "Enter an article URL and we'll summarize it automatically.",
    "tool.length": "Length",
    "tool.length.short": "Short",
    "tool.length.medium": "Medium",
    "tool.length.long": "Long",
    "tool.tone": "Tone",
    "tool.tone.simple": "Simple",
    "tool.tone.academic": "Academic",
    "tool.tone.professional": "Professional",
    "tool.format": "Output format",
    "tool.format.paragraph": "Paragraph",
    "tool.format.bullets": "Bullet points",
    "tool.format.keysentences": "Key sentences",
    "tool.format.tldr": "TL;DR",
    "tool.language": "Language",
    "tool.submit": "Summarize now",
    "tool.loading": "Analyzing…",
    "tool.result.title": "Your summary",
    "tool.result.words": "words",
    "tool.copy": "Copy",
    "tool.copied": "Copied!",
    "tool.minchars": "Minimum {min} characters",
    "tool.maxchars": "Maximum {max} characters",
    "tool.error.backend": "Backend configuration missing.",
    "tool.error.network": "Network error. Check your connection and try again.",
    "tool.error.default": "Error during summarization. Please try again.",
    // HowItWorks
    "how.title": "How it works",
    "how.subtitle": "Three steps, five seconds, zero hassle",
    "how.step1.title": "Paste your text",
    "how.step1.desc": "Copy from PDFs, websites, notes or emails. Or paste a URL.",
    "how.step2.title": "Choose your style",
    "how.step2.desc": "Length, tone, format, and language. You decide how you want the result.",
    "how.step3.title": "Get the summary",
    "how.step3.desc": "AI generates your summary in seconds. Copy, download PDF, or share.",
    // Meta
    "meta.title": "AI Text Summarizer Online – Free Professional Writing Tool",
    "meta.description": "Free AI text summarizer for students and professionals: paste text, a URL or a PDF and get key points, bullets or a TL;DR. No signup required.",
    // CTA
    "cta.title": "Try our text summarizer for free",
    "cta.subtitle": "Summarize any text in seconds with our AI.",
    "cta.button": "Summarize now →",
    // Blog
    "blog.title": "Blog",
    "blog.badge": "Guides & Insights",
    "blog.subtitle": "Tips, techniques and insights on summarizing text and studying smarter.",
    "blog.read": "Read article",
    // Footer
    "footer.tagline": "Summarize text with AI",
    "footer.guide": "Complete Guide",
    "footer.rights": "All rights reserved.",
    "footer.desc": "Free AI-powered text summarization tool.",
    // Parafrasatore
    "paraphrase.title": "Free Online Paraphrasing Tool",
    "paraphrase.subtitle": "Rewrite your text with different words while keeping the original meaning.",
    // Contatore
    "wordcount.title": "Online Word Counter",
    "wordcount.subtitle": "Count words, characters, sentences and paragraphs in real time.",
    // Semplificatore
    "simplify.title": "Simplify Text Online",
    "simplify.subtitle": "Transform complex text into clear, easy-to-understand versions.",
  },
};
